import React, { useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Globe2, MinusCircle } from 'lucide-react';

const BV_LABELS = {
  truckwash: 'Truckwash',
  truckstop: 'Truckstop',
  truckshop: 'Truckshop',
  truckssupply: 'Truckssupply',
};

const STATUS_LABELS = {
  actief: null, // no badge
  gepland: { label: 'Gepland', tone: 'bg-amber-50 text-amber-700 border-amber-100' },
  inactief: { label: 'Inactief', tone: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const ADMIN_ROLES = new Set(['super_owner', 'owner', 'admin']);

/**
 * Grouped checkbox grid for selecting locations.
 *
 * Props:
 *   locations: Array<{slug, display_name, bv_id, status}>
 *   selected:  Array<string>  — currently selected slugs
 *   role:      string         — globale_rol of the target user
 *   onChange:  (next: string[]) => void
 */
export default function LocationsSelector({ locations = [], selected = [], role, onChange }) {
  // Group + alphabetically sort locations by display_name within each BV
  const grouped = useMemo(() => {
    const map = {};
    for (const loc of locations) {
      const bv = loc.bv_id || 'overig';
      if (!map[bv]) map[bv] = [];
      map[bv].push(loc);
    }
    Object.values(map).forEach(arr =>
      arr.sort((a, b) => a.display_name.localeCompare(b.display_name, 'nl'))
    );
    return map;
  }, [locations]);

  // Order of BV sections (truckwash first, truckssupply last)
  const bvOrder = ['truckwash', 'truckstop', 'truckshop', 'truckssupply'];
  const orderedBvs = [
    ...bvOrder.filter(b => grouped[b]),
    ...Object.keys(grouped).filter(b => !bvOrder.includes(b)).sort(),
  ];

  const isAdminRole = ADMIN_ROLES.has(role);
  const totalActive = locations.filter(l => l.status === 'actief').length;
  const isUnlimited = selected.length === 0;
  const showUnlimitedWarning = !isAdminRole && isUnlimited;

  const toggle = (slug) => {
    if (selected.includes(slug)) onChange(selected.filter(s => s !== slug));
    else onChange([...selected, slug]);
  };

  const selectAll = (bvSlugs) => {
    const set = new Set(selected);
    bvSlugs.forEach(s => set.add(s));
    onChange([...set]);
  };

  const deselectAll = (bvSlugs) => {
    const removeSet = new Set(bvSlugs);
    onChange(selected.filter(s => !removeSet.has(s)));
  };

  return (
    <div className="space-y-4" data-testid="locations-selector">
      {/* Explanation */}
      <p className="text-xs text-slate-500 leading-relaxed">
        {isAdminRole ? (
          <>Lege selectie = toegang tot alle locaties (standaard voor admin-rollen).</>
        ) : (
          <>Selecteer specifieke locaties waar deze gebruiker toegang toe heeft.</>
        )}
      </p>

      {/* Quick action bar */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={isAdminRole ? 'default' : 'outline'}
          onClick={() => onChange([])}
          className={
            isAdminRole
              ? 'h-8 bg-[#1a2332] hover:bg-[#111827] text-white rounded-lg text-xs'
              : 'h-8 rounded-lg text-xs'
          }
          data-testid="locations-quick-all"
        >
          <Globe2 className="w-3.5 h-3.5 mr-1.5" /> Alle locaties
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onChange([])}
          className="h-8 rounded-lg text-xs text-slate-600"
          data-testid="locations-quick-none"
          // hidden when already empty
          style={isUnlimited ? { display: 'none' } : {}}
        >
          <MinusCircle className="w-3.5 h-3.5 mr-1.5" /> Alle deselecteren
        </Button>
        {selected.length > 0 && (
          <span className="text-xs text-slate-400 ml-auto">
            {selected.length} van {totalActive} geselecteerd
          </span>
        )}
      </div>

      {/* Unlimited-access warning for non-admin roles */}
      {showUnlimitedWarning && (
        <div
          className="flex items-start gap-2 bg-amber-50 border border-amber-100 text-amber-900 text-xs rounded-lg px-3 py-2"
          data-testid="locations-unlimited-warning"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
          <span>
            Deze gebruiker krijgt toegang tot alle {totalActive} locaties. Bedoeld voor
            admin-rollen — controleer of dit klopt bij rol &apos;{role}&apos;.
          </span>
        </div>
      )}

      {/* Grouped checkbox grid */}
      <div className="space-y-4">
        {orderedBvs.map(bv => {
          const bvSlugs = grouped[bv].map(l => l.slug);
          const allChecked = bvSlugs.every(s => selected.includes(s));
          const noneChecked = bvSlugs.every(s => !selected.includes(s));
          return (
            <div key={bv} data-testid={`locations-bv-${bv}`}>
              <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-slate-100">
                <h4 className="text-xs font-semibold text-[#1a2332] uppercase tracking-wider">
                  {BV_LABELS[bv] || bv}
                  <span className="ml-2 text-slate-400 font-normal normal-case tracking-normal">
                    ({grouped[bv].length})
                  </span>
                </h4>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => selectAll(bvSlugs)}
                    disabled={allChecked}
                    className="text-[11px] text-slate-500 hover:text-[#1a2332] disabled:text-slate-300 disabled:cursor-not-allowed transition-colors"
                    data-testid={`locations-bv-${bv}-select-all`}
                  >
                    Alles
                  </button>
                  <span className="text-slate-300 text-[11px]">·</span>
                  <button
                    type="button"
                    onClick={() => deselectAll(bvSlugs)}
                    disabled={noneChecked}
                    className="text-[11px] text-slate-500 hover:text-[#1a2332] disabled:text-slate-300 disabled:cursor-not-allowed transition-colors"
                    data-testid={`locations-bv-${bv}-deselect-all`}
                  >
                    Geen
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-1.5">
                {grouped[bv].map(loc => {
                  const checked = selected.includes(loc.slug);
                  const statusBadge = STATUS_LABELS[loc.status];
                  return (
                    <label
                      key={loc.slug}
                      className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer hover:text-[#1a2332] py-0.5"
                      title={loc.slug}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggle(loc.slug)}
                        data-testid={`locations-checkbox-${loc.slug}`}
                      />
                      <span className="truncate">{loc.display_name}</span>
                      {statusBadge && (
                        <Badge variant="outline" className={`text-[10px] py-0 px-1.5 ${statusBadge.tone}`}>
                          {statusBadge.label}
                        </Badge>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
